import { TimeFormatOptions } from 'shared/types/date-format-options';

import { isNumber } from './is-number';

export const processTime = (value: string, isMilitaryTime: boolean) => {
  const cleanedString = value.trim();

  // --- Attempt Standard Time Parsing First (if not military time) ---
  if (!isMilitaryTime) {
    const standardTimeRegex =
      /(\d{1,2})(?:[:.]?(\d{2}))?\s*(am|pm|AM|PM|Am|Pm|aM|pM|a.m|p.m|A.M|P.M)?/;
    const match = cleanedString.match(standardTimeRegex);

    if (match) {
      const [_, h, m, ap] = match;
      const hours = Number(h);
      const minutes = Number(m);
      return hours <= 12 && minutes <= 59 && ap
        ? `${h}:${m} ${ap.toUpperCase()}`
        : null;
    }
    return null;
  }

  // military time
  const digitOnlyValue = value.replace(/[^\d:]/g, '');
  // Add the ':' if it is missing and trim off all excessive digits
  const hours = digitOnlyValue.slice(0, 2);
  const minutes = digitOnlyValue.includes(':')
    ? digitOnlyValue.slice(3, 5)
    : digitOnlyValue.slice(2, 4);
  const hoursNumber = Number(hours);
  const minutesNumber = Number(minutes);

  if (
    !isNumber(hoursNumber) ||
    hoursNumber > 23 ||
    !isNumber(minutesNumber) ||
    minutesNumber > 59
  ) {
    return null;
  }

  return !digitOnlyValue.includes(':') && digitOnlyValue.length > 2
    ? digitOnlyValue.slice(0, 2) + ':' + digitOnlyValue.slice(2, 4)
    : digitOnlyValue.slice(0, 5);
};

const limits = {
  digits: 4,
  colons: 1,
  spaces: 1,
  ap: 1,
  mM: 1,
};

const filterValueOnNonsense = (value: string) => {
  // Initialize counts and filter out invalid characters first
  const counts = { digits: 0, colons: 0, spaces: 0, ap: 0, mM: 0 };
  const initiallyFiltered = value.replace(/[^0-9:aApPmM. ]/g, '');

  return Array.from(initiallyFiltered).reduce((output, char) => {
    if (/\d/.test(char) && counts.digits++ < limits.digits) {
      return output + char;
    }
    if (char === ':' && counts.colons++ < limits.colons) {
      return output + char;
    }
    if (char === ' ' && counts.spaces++ < limits.spaces) {
      return output + char;
    }
    if (/[aApP]/.test(char) && counts.ap++ < limits.ap) {
      return output + char;
    }
    if (/[mM]/.test(char) && counts.mM++ < limits.mM) {
      return output + char;
    }
    // Optionally, if you want to keep other allowed characters without a max count, e.g., periods:
    // if (char === '.') return output + char;
    return output; // Discard character if it doesn't match or limit is reached
  }, '');
};

export const processStringToHoursAndMinutes = (
  value: string,
  timeFormat: TimeFormatOptions,
) => {
  // clear the string of nonsense
  const filteredValue = filterValueOnNonsense(value);

  const parsedValue = processTime(
    filteredValue,
    timeFormat === TimeFormatOptions.Military,
  );

  return parsedValue || filteredValue;
};
